import useSWR from 'swr';
import { useMemo } from 'react';
import { GridColDef } from '@mui/x-data-grid-pro';
import { GridPreProcessEditCellProps } from '@mui/x-data-grid/models/params/gridCellParams';

import { renderBooleanCell, dataGridCol } from '@shared/components/grid/common';
import { GridColNumber } from '@shared/components/grid/GridColNumber';
import { GridColBoolean } from '@shared/components/grid/GridColBoolean';
import { deCamelize, gridDateTimeFormatter, newGridRecordId } from '@shared/commonFunctions';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { GridColSelect } from '@shared/components/grid/GridColSelect';
import { IOption } from '@shared/interfaces';
import { OPERATOR_SYMBOL, OperatorType, operatorsList } from '@shared/constants/operatorsList';
import { LocalTabContent } from '@styles/CommonStyles';

import { ILenderUtilityTabProps } from '../LenderUtilityTabs';
import { TLenderCreditRule } from '../../interfaces';
import { creditRulesValidationSchema } from '../../validation';
import { requestCreateCreditRule, requestDeleteCreditRule, requestUpdateCreditRule } from './queries';

type TCreditRuleFieldType = 'Text' | 'Boolean';

interface ICreditRule {
  creditRuleType: string;
  fieldType: TCreditRuleFieldType;
}

export const CreditRules = ({ data, lenderId }: ILenderUtilityTabProps) => {
  const { data: creditRuleTypes = [] } = useSWR<ICreditRule[]>('/lender-service/lenders/creditRules');

  const existingRules = new Set();

  const booleanRules = new Set(
    creditRuleTypes.filter((rule) => rule.fieldType === 'Boolean').map((rule) => rule.creditRuleType),
  );

  const isBooleanField = (value?: string) => value && booleanRules.has(value);

  const lenderCreditRules = useMemo(
    () => (data?.lenderCreditRules ?? []).map((rule) => ({
      ...rule,
      creditRuleType: rule.creditRule.creditRuleType,
      isBooleanField: isBooleanField(rule.creditRule.creditRuleType)
    })),
    [data?.lenderCreditRules, isBooleanField]
  );
  
  lenderCreditRules?.forEach(({ creditRuleType }) => {
    existingRules.add(creditRuleType);
  });

  const creditRules: IOption[] = useMemo(
    () =>
      creditRuleTypes.map((type) => ({
        value: type.creditRuleType,
        label: deCamelize(type.creditRuleType),
        disabled: existingRules.has(type.creditRuleType),
      })),
    [creditRuleTypes, existingRules],
  );

  const preProcessGreenValue = ({ props, otherFieldsProps }: GridPreProcessEditCellProps) => ({
    ...props,
    value: booleanRules.has(otherFieldsProps?.creditRuleType?.value)
      ? !!props.value
      : typeof props.value !== 'boolean'
      ? props.value
      : null,
  });

  const preProcessYellowValue = ({ props, otherFieldsProps }: GridPreProcessEditCellProps) => ({
    ...props,
    value: booleanRules.has(otherFieldsProps?.creditRuleType?.value) ? undefined : props.value,
  });

  const preProcessCondition = ({ props, otherFieldsProps }: GridPreProcessEditCellProps) => ({
    ...props,
    value: isBooleanField(otherFieldsProps?.creditRuleType?.value) ? OperatorType.Equal : props.value,
  });

  const preProcessIsBoolean = ({ props, otherFieldsProps }: GridPreProcessEditCellProps) => ({
    ...props,
    value: isBooleanField(otherFieldsProps?.creditRuleType?.value),
  });

  const columns: GridColDef[] = [
    dataGridCol('creditRuleType', {
      minWidth: 264,
      renderCell: (params) => deCamelize(params.value),
      renderEditCell: (params) => <GridColSelect params={params} options={creditRules} />,
    }),
    dataGridCol('condition', {
      required: true,
      minWidth: 180,
      renderCell: (params) => deCamelize(params.value),
      preProcessEditCellProps: preProcessCondition,
      renderEditCell: (params) => (
        <GridColSelect
          params={params}
          disabled={booleanRules.has(params.row.creditRuleType)}
          options={
            booleanRules.has(params.row.creditRuleType)
              ? [{ value: OperatorType.Equal, label: OPERATOR_SYMBOL[OperatorType.Equal] }]
              : operatorsList
          }
        />
      ),
    }),
    dataGridCol('value', {
      minWidth: 88,
      flex: 1,
      preProcessEditCellProps: preProcessGreenValue,
      valueGetter: (params) => (isBooleanField(params.row.creditRuleType) ? params.value === 'true' ? true : false : params.value),
      renderCell: (params) => (isBooleanField(params.row.creditRuleType) ? renderBooleanCell(params) : params.value),
      renderEditCell: (params) =>
        isBooleanField(params.row.creditRuleType) ? (
          <GridColBoolean params={params} />
        ) : (
          <GridColNumber params={params} limit={9} />
        ),
    }),
    dataGridCol('yellowValue', {
      minWidth: 88,
      flex: 1,
      preProcessEditCellProps: preProcessYellowValue,
      renderEditCell: (params) =>
        !isBooleanField(params.row.creditRuleType) && <GridColNumber params={params} limit={9} />,
    }),
    dataGridCol('createdDate', {
      editable: false,
      minWidth: 180,
      type: 'dateTime',
      valueFormatter: gridDateTimeFormatter,
    }),
    dataGridCol('isBooleanField', {
      hide: true,
      preProcessEditCellProps: preProcessIsBoolean,
      valueGetter: ({row}) => isBooleanField(row.creditRuleType),
    })
  ].map((column) => ({ editable: true, ...column }));

  const gridActions = {
    onSave: (row: TLenderCreditRule) =>
      row?.id === newGridRecordId ? requestCreateCreditRule(lenderId, row) : requestUpdateCreditRule(lenderId, row),
    onDelete: (row: TLenderCreditRule) => requestDeleteCreditRule(lenderId, row?.id),
  };

  return (
    <LocalTabContent>
      <PhoenixGrid
        autoHeight
        withHeader
        name="Credit Rule"
        gridId={`Credit Rule-${lenderId}`}
        pageSize={25}
        columns={columns}
        loading={!lenderCreditRules}
        rows={lenderCreditRules}
        actions={gridActions}
        validateSchema={creditRulesValidationSchema}
        disabled={existingRules.size === creditRules.length}
      />
    </LocalTabContent>
  );
};
